import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiUrl } from '../api/client-modules';
import { authedFetch } from '../auth/supabase-browser';
import { useAuth } from '../auth/AuthProvider';
import {
  resolveSelectedClientId,
  type ClientOrganization,
} from './organization-selection';

const STORAGE_KEY = 'client-oneup:selected-client-id';

interface OrganizationContextValue {
  organizations: ClientOrganization[];
  selectedClientId: string | null;
  isLoading: boolean;
  error?: string;
  selectClient: (clientId: string) => void;
  reload: () => void;
}

const OrganizationContext = createContext<OrganizationContextValue | null>(null);

type Decoration = Pick<ClientOrganization, 'website' | 'logoUrl'>;

const EMPTY_ORGANIZATIONS: ClientOrganization[] = [];

/**
 * The selected organization, and the list it is selected from.
 *
 * The list comes from the auth bootstrap rather than its own request. That is
 * the whole point: this provider used to fire `/api/client/organizations` — a
 * request that waits on a per-organization Airtable lookup — and to empty
 * itself first every time `accessState` left `allowed`. Since Supabase re-emits
 * `SIGNED_IN` whenever the tab regains focus, simply looking away from the
 * portal and back discarded the selected organization and reloaded the list.
 *
 * Now the identity of the organizations is already in hand when access is
 * granted, and only logos and websites are fetched — in the background, after
 * the shell is up, and never blocking it.
 */
export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { actor, accessState, organizations: bootstrapOrganizations, refreshAccess } = useAuth();
  const authUserId = actor?.userId;
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [error, setError] = useState<string>();

  const decorationQuery = useQuery({
    queryKey: ['client-organization-decorations', authUserId],
    queryFn: async (): Promise<ClientOrganization[]> => {
      const response = await authedFetch(apiUrl('/api/client/organizations'), {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('Organization metadata is temporarily unavailable.');
      const body = await response.json().catch(() => null) as {
        organizations?: ClientOrganization[];
      } | null;
      if (!Array.isArray(body?.organizations)) {
        throw new Error('Organization metadata is temporarily unavailable.');
      }
      return body.organizations;
    },
    enabled: Boolean(authUserId && accessState === 'allowed'),
    staleTime: 5 * 60_000,
    retry: 2,
    refetchOnWindowFocus: false,
  });
  const fetched = authUserId
    ? decorationQuery.data ?? (decorationQuery.isError ? [] : null)
    : null;

  /*
   * The bootstrap array is rebuilt on every background revalidation. Holding
   * the last equal value keeps its identity stable, so a token refresh or a tab
   * return does not cascade re-renders (or a decoration refetch) through the
   * whole workspace for data that did not change.
   *
   * The fallback to the fetched list exists so the browser bundle and the
   * Worker can be deployed in either order: a Worker that predates the
   * bootstrap field simply omits it, and the portal loads the list the old way
   * instead of telling every client they have no organizations.
   */
  const bootstrap = authUserId && accessState !== 'denied' ? bootstrapOrganizations : EMPTY_ORGANIZATIONS;
  const base = useStableOrganizations(
    bootstrap.length > 0 ? bootstrap : (authUserId && fetched) || EMPTY_ORGANIZATIONS,
  );

  const decorations = useMemo(() => Object.fromEntries(
    (fetched ?? []).map((organization): [string, Decoration] => [
      organization.clientId,
      { website: organization.website, logoUrl: organization.logoUrl },
    ]),
  ), [fetched]);

  const organizations = useMemo(
    () => base.map((organization) => ({ ...organization, ...decorations[organization.clientId] })),
    [base, decorations],
  );

  // Keep the current selection if it is still authorized; otherwise fall back
  // to the saved one, then to the first organization. Deliberately does not
  // reset on `accessState` — a revalidation is not a reason to move the user.
  useEffect(() => {
    if (!authUserId) {
      setSelectedClientId(null);
      return;
    }
    const storageKey = `${STORAGE_KEY}:${authUserId}`;
    setSelectedClientId((current) => {
      if (current && base.some((organization) => organization.clientId === current)) return current;
      const resolved = resolveSelectedClientId(base, window.localStorage.getItem(storageKey));
      if (resolved) window.localStorage.setItem(storageKey, resolved);
      else window.localStorage.removeItem(storageKey);
      return resolved;
    });
  }, [authUserId, base]);

  useEffect(() => {
    if (accessState !== 'allowed') return;
    setError(base.length === 0 && fetched !== null
      ? 'No active organization membership is linked to this account.'
      : undefined);
  }, [accessState, base.length, fetched]);

  const selectClient = useCallback((clientId: string) => {
    if (!authUserId) return;
    if (!base.some((organization) => organization.clientId === clientId)) return;
    window.localStorage.setItem(`${STORAGE_KEY}:${authUserId}`, clientId);
    setSelectedClientId(clientId);
  }, [authUserId, base]);

  const reload = useCallback(() => {
    void decorationQuery.refetch();
    refreshAccess();
  }, [decorationQuery, refreshAccess]);

  const value = useMemo<OrganizationContextValue>(() => ({
    organizations,
    selectedClientId,
    // Normally false: the list arrives with the access decision, so there is
    // nothing to wait for. It is only ever true while falling back for a Worker
    // that predates the bootstrap field.
    isLoading: accessState === 'allowed' && base.length === 0 && fetched === null,
    error,
    selectClient,
    reload,
  }), [organizations, selectedClientId, accessState, base.length, fetched, error, selectClient, reload]);

  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>;
}

/** Return the previous array whenever the new one is equal to it. */
function useStableOrganizations(next: ClientOrganization[]): ClientOrganization[] {
  const previous = useRef<ClientOrganization[]>(EMPTY_ORGANIZATIONS);
  if (!sameOrganizations(previous.current, next)) previous.current = next;
  return previous.current;
}

function sameOrganizations(a: ClientOrganization[], b: ClientOrganization[]): boolean {
  return a.length === b.length && a.every((organization, index) =>
    organization.clientId === b[index]?.clientId
    && organization.displayName === b[index]?.displayName
    && organization.role === b[index]?.role
    && organization.status === b[index]?.status
    && organization.capabilities?.planKey === b[index]?.capabilities?.planKey
    && JSON.stringify(organization.capabilities?.features ?? null)
      === JSON.stringify(b[index]?.capabilities?.features ?? null));
}

export function useOrganization(): OrganizationContextValue {
  const value = useContext(OrganizationContext);
  if (!value) throw new Error('useOrganization must be used within an OrganizationProvider.');
  return value;
}

export function withClientId(path: string, clientId: string): string {
  const url = new URL(path, 'https://client-portal.invalid');
  url.searchParams.set('client_id', clientId);
  return `${url.pathname}${url.search}`;
}
